//src/components/gro/SurveyDimensionsSection.tsx
import { BarChart3, HelpCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import type { SurveyCampaignDimensionResult } from "@/types/gro";

type Props = {
  dimensions?: SurveyCampaignDimensionResult[] | null;
  maxScore?: number;
};

type RiskPresentation = {
  text: string;
  className: string;
};

function formatScore(value: number) {
  return Number(value.toFixed(2)).toString();
}

function riskFromAverage(avg: number): RiskPresentation {
  if (avg < 2) {
    return {
      text: "Risco Crítico",
      className: "border-red-200 bg-red-50 text-red-700 hover:bg-red-50",
    };
  }

  if (avg < 3) {
    return {
      text: "Risco Alto",
      className: "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50",
    };
  }

  if (avg < 4) {
    return {
      text: "Risco Moderado",
      className: "border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-100",
    };
  }

  return {
    text: "Risco Baixo",
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
  };
}

export function SurveyDimensionsSection({ dimensions, maxScore = 5 }: Props) {
  const safeDimensions = Array.isArray(dimensions) ? dimensions : [];

  if (safeDimensions.length === 0) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        Ainda não há resultados por dimensão.
      </Card>
    );
  }

  const overallAvg =
    safeDimensions.length > 0
      ? +(
          safeDimensions.reduce((sum, dimension) => sum + dimension.average_score, 0) /
          safeDimensions.length
        ).toFixed(2)
      : 0;

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-primary" />
          <span className="font-semibold">Resultados por dimensão</span>
        </div>

        <HoverCard>
          <HoverCardTrigger asChild>
            <button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted">
              <HelpCircle className="h-3.5 w-3.5" />
              Como calculamos?
            </button>
          </HoverCardTrigger>

          <HoverCardContent className="w-[420px] text-sm" side="left">
            <p className="mb-2 font-semibold">Como a média por dimensão é calculada</p>

            <ol className="list-decimal space-y-1.5 pl-4 text-xs text-muted-foreground">
              <li>
                Cada dimensão agrupa perguntas do questionário. Cada respondente pode
                ter respondido a um subconjunto dessas perguntas.
              </li>
              <li>
                Para cada respondente, calculamos a média dos pesos das respostas
                dentro daquela dimensão.
              </li>
              <li>
                A <strong className="text-foreground">média da dimensão</strong> é a
                média aritmética das médias individuais de todos os respondentes.
              </li>
            </ol>

            <div className="mt-3 rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
              <p className="mb-1 font-medium text-foreground">Faixas de risco desta aba</p>
              <p>
                <strong className="text-foreground">Risco Baixo</strong>: média maior
                ou igual a 4
              </p>
              <p>
                <strong className="text-foreground">Risco Moderado</strong>: média de 3
                até 3,99
              </p>
              <p>
                <strong className="text-foreground">Risco Alto</strong>: média de 2 até
                2,99
              </p>
              <p>
                <strong className="text-foreground">Risco Crítico</strong>: média menor
                que 2
              </p>
            </div>

            <div className="mt-3 rounded-md border bg-muted/50 p-3 text-xs text-muted-foreground">
              <p className="mb-1 font-medium text-foreground">Exemplo:</p>
              <p>
                Dimensão "Apoio dos Colegas" com 6 perguntas e 2 respondentes, cada um
                respondeu 3 perguntas:
              </p>
              <p className="mt-1">
                Respondente 1: (2 + 3 + 2) / 3 ={" "}
                <strong className="text-foreground">2,33</strong>
              </p>
              <p>
                Respondente 2: (3 + 4 + 4) / 3 ={" "}
                <strong className="text-foreground">3,67</strong>
              </p>
              <p className="mt-1">
                Média da dimensão: (2,33 + 3,67) / 2 ={" "}
                <strong className="text-foreground">3,00</strong>
              </p>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {safeDimensions.map((dimension, index) => {
          const risk = riskFromAverage(dimension.average_score);
          const pct = Math.min((dimension.average_score / maxScore) * 100, 100);

          return (
            <div
              key={dimension.dimension_id || index}
              className="group rounded-xl border p-4 transition-colors hover:bg-muted/30"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium">
                  {dimension.dimension_name ||
                    dimension.dimension_code ||
                    "Sem dimensão"}
                </div>

                <Badge
                  variant="outline"
                  className={`shrink-0 text-[10px] ${risk.className}`}
                >
                  {risk.text}
                </Badge>
              </div>

              <div className="mt-3 space-y-1.5">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Média</span>
                  <span className="text-sm font-semibold text-foreground">
                    {formatScore(dimension.average_score)}
                    <span className="text-xs font-normal text-muted-foreground">
                      {" "}
                      / {maxScore}
                    </span>
                  </span>
                </div>

                <Progress value={pct} className="h-2" />
              </div>

              <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
                <span>Min: {formatScore(dimension.min_score)}</span>
                <span className="text-border">•</span>
                <span>Max: {formatScore(dimension.max_score)}</span>
                <span className="text-border">•</span>
                <span>{dimension.responses_count} respostas</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-lg border bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Média geral:</span>{" "}
        ({safeDimensions.map((dimension) => formatScore(dimension.average_score)).join(" + ")}) /{" "}
        {safeDimensions.length} ={" "}
        <span className="text-sm font-bold text-primary">
          {formatScore(overallAvg)}
        </span>
      </div>
    </Card>
  );
}
